import { type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Storage/SdCardSettings/SdCardSettings.less";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import {
	Button, ButtonTypes, Input, type SelectOption, ProgressBar, Select, Switch, ConfirmModal, SettingsLayout, Card
} from "@clintonelec/react-storybook";
import { DiskWriteMode, FormatType } from "Data/Objects/Storage";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectSdCard, setSdCardAction } from "Data/Redux/Slices/Settings/Storage/SdCard";
import { isEqual, merge } from "lodash";
import { produce } from "immer";
import type { SdCardSettingsState } from "Interfaces";

const diskWriteModeOptions: SelectOption[] = [
	{
		label: "Once",
		value: DiskWriteMode.ONCE
	},
	{
		label: "Overwrite",
		value: DiskWriteMode.OVERWRITE
	}
];

const formatTypeOptions: SelectOption[] = [
	{
		label: FormatType.EXFAT,
		value: FormatType.EXFAT
	}
];

const modalContent = "All recorded data will be removed. Do you want to continue?";

function SdCardSettings() {
	const dispatch = useAppDispatch();
	const sdCardSettings = useAppSelector(selectSdCard);
	const [ sdCardSettingsDiff, setSdCardSettingsDiff ] = useState<Partial<SdCardSettingsState>>({});

	const setSdCardSettings = (newSettings: SdCardSettingsState) => {
		dispatch(setSdCardAction(newSettings));
	};

	const localSdCardSettings = produce(sdCardSettings, draft => {
		merge(draft, sdCardSettingsDiff);
	});

	const {
		enableSdCardRecording, diskWriteMode, storageName, formatType, totalSize, usedSize, status
	} = localSdCardSettings;

	const disabled = isEqual(localSdCardSettings, sdCardSettings);

	const statusMessage = () => {
		if (!status) {
			return "No SD Card";
		} else if (status && !enableSdCardRecording) {
			return "Not In Use";
		} else if (status && enableSdCardRecording) {
			return "Recording";
		} else {
			return "Error";
		}
	};

	const boxRowClassName = `${ status ? "box-row" : "hidden" }`;
	const progressBarClassName = `${ status ? "" : "hidden" }`;

	const handleReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setSdCardSettingsDiff({});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setSdCardSettings(localSdCardSettings);
		setSdCardSettingsDiff({});
	};

	const handleEnableSdCardRecordingChange = (checked: boolean) => {
		setSdCardSettingsDiff(produce(sdCardSettingsDiff, draft => {
			draft.enableSdCardRecording = checked;
		}));
	};

	const handleStatusChangeClick = () => {
		setSdCardSettingsDiff(produce(sdCardSettingsDiff, draft => {
			draft.status = !status;
		}));
	};

	const handleDiskWriteModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setSdCardSettingsDiff(produce(sdCardSettingsDiff, draft => {
			draft.diskWriteMode = value as DiskWriteMode;
		}));
	};

	const handleStorageNameUpdate = (value: string) => {
		setSdCardSettingsDiff(produce(sdCardSettingsDiff, draft => {
			draft.storageName = value;
		}));
	};

	const handleFormatTypeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setSdCardSettingsDiff(produce(sdCardSettingsDiff, draft => {
			draft.formatType = value as FormatType;
		}));
	};

	return (
		<SettingsLayout.Form className="sd-card-settings" onReset={ handleReset } onSubmit={ handleSubmit }>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="SD Card">
					<div className="form-row">
						<Switch
							checked={ enableSdCardRecording }
							className="sd-card-switch"
							onChange={ handleEnableSdCardRecordingChange }
						/>
						<span>Enable SD Card Recording</span>
					</div>
					<div className="sd-card-settings-wrapper">
						<div className="sd-card-settings">
							<p>SD Card Info</p>
							<div className="sd-card-info-box">
								<div className={ boxRowClassName }>
									<span>Total Size</span>
									<span>{ totalSize } GB</span>
								</div>
								<div className={ progressBarClassName }>
									<ProgressBar
										showProgress={ false }
										progress={ usedSize / totalSize * 100 }
									/>
								</div>
								<div className={ boxRowClassName }>
									<span>{ usedSize } GB Used</span>
									<span>{ totalSize - usedSize } GB Free</span>
								</div>
								<div className={ boxRowClassName }>
									<span>Format</span>
									<span>{ formatType }</span>
								</div>
								<div className="box-row">
									<span>Status</span>
									<span>{ statusMessage() }</span>
								</div>
							</div>
							<div className="mount-button">
								<Button
									disabled={ !enableSdCardRecording }
									htmlType="button"
									onClick={ handleStatusChangeClick }
									type={ ButtonTypes.TERTIARY }
								>
									{ status ? "Unmount" : "Mount" }
								</Button>
							</div>
						</div>
						<div className="sd-card-settings">
							<div className="form-row">
								<span>Disk Write Mode</span>
								<Select
									allowClear={ false }
									className="form-select"
									disabled={ !enableSdCardRecording }
									onSelect={ handleDiskWriteModeSelect }
									options={ diskWriteModeOptions }
									value={ diskWriteMode }
								/>
							</div>
							<div className="form-row">&nbsp;</div>
							<div className="form-row">
								<span>Storage Name</span>
								<Input
									disabled={ !enableSdCardRecording }
									onUpdate={ handleStorageNameUpdate }
									value={ storageName }
									wrapClassName="form-input"
								/>
							</div>
							<div className="form-row">
								<span>Format Type</span>
								<div className="select-button-wrapper form-select">
									<Select
										allowClear={ false }
										disabled={ !status || !enableSdCardRecording }
										onSelect={ handleFormatTypeSelect }
										options={ formatTypeOptions }
										value={ formatType }
									/>
									<ConfirmModal
										cancelButton={ { text: "Cancel", type: ButtonTypes.DANGER } }
										modalContent={ modalContent }
										okButton={ { text: "Confirm", type: ButtonTypes.TERTIARY } }
										title={ "Confirm SD Card Format" }
									>
										<Button
											disabled={ !status || !enableSdCardRecording }
											htmlType="button"
											type={ ButtonTypes.TERTIARY }
										>
											Format
										</Button>
									</ConfirmModal>
								</div>
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(SdCardSettings);
